import {
  decodePDFRawStream,
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFName,
  PDFRawStream,
  PDFTextField,
  StandardFonts,
  TextAlignment,
  rgb,
} from 'pdf-lib';

export function normalizeRetainerAmount(value, label = 'Amount') {
  if (!['string', 'number'].includes(typeof value)) throw new Error(`${label} is required.`);
  const text = String(value).trim();
  if (!text) throw new Error(`${label} is required.`);
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(text)) {
    throw new Error(`${label} must be a non-negative amount with no more than two decimal places.`);
  }
  const amount = Number(text.replace(/,/g, ''));
  if (!Number.isFinite(amount) || !Number.isSafeInteger(Math.round(amount * 100))) throw new Error(`${label} is too large.`);
  return amount.toFixed(2);
}

function supportedText(font, value, label) {
  const text = String(value || '').trim();
  try {
    font.encodeText(text);
  } catch {
    throw new Error(`${label} contains a character this retainer template cannot print. Please contact the firm to prepare a compatible PDF; the name has not been changed.`);
  }
  return text;
}

function wrapName(text, font, size, width) {
  const lines = [];
  let current = '';
  for (const word of text.split(/\s+/)) {
    if (font.widthOfTextAtSize(current ? `${current} ${word}` : word, size) <= width) {
      current = current ? `${current} ${word}` : word;
      continue;
    }
    if (current) lines.push(current);
    current = '';
    for (const character of word) {
      if (current && font.widthOfTextAtSize(current + character, size) > width) {
        lines.push(current);
        current = '';
      }
      current += character;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function removeOriginalSignatureBlock(pdf, page, anchor) {
  const contents = page.node.Contents();
  const references = contents instanceof PDFArray ? contents.asArray() : [contents];
  let replaced = false;
  const retained = references.map(reference => {
    const stream = pdf.context.lookup(reference);
    if (!(stream instanceof PDFRawStream)) return reference;
    const decodedBytes = decodePDFRawStream(stream).decode();
    const source = Array.from(decodedBytes, byte => String.fromCharCode(byte)).join('');
    // Both supplied templates end their legal text before this signature-label
    // clipping rectangle. Its top meets the existing debtor-name widget's
    // bottom. Remove that final block, including the template's typed s/ text,
    // rather than leaving an electronic signature hidden behind a white box.
    const clips = /\bq\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+re\s+W\s+n\s+BT\b/g;
    let match;
    while ((match = clips.exec(source))) {
      const [x, y, width, height] = match.slice(1).map(Number);
      if (Math.abs(x - anchor.x) > 2 || Math.abs(width - anchor.width) > 2
        || Math.abs(y + height - anchor.y) > 2 || height < 40 || height > 80) continue;
      if (replaced) throw new Error('The retainer template has an ambiguous signature area. Please contact the firm.');
      replaced = true;
      return pdf.context.register(pdf.context.flateStream(decodedBytes.slice(0, match.index)));
    }
    return reference;
  });
  if (!replaced) throw new Error('The retainer template signature area could not be verified. Please contact the firm before printing.');
  page.node.set(PDFName.of('Contents'), pdf.context.obj(retained));
}

function fitFormText(form, name, value, font, alignment = TextAlignment.Right) {
  const field = form.getTextField(name);
  const widths = field.acroField.getWidgets().map(widget => widget.getRectangle().width - 4);
  const width = Math.min(...widths);
  let size = 10;
  while (size > 6 && font.widthOfTextAtSize(value, size) > width) size -= 0.25;
  if (font.widthOfTextAtSize(value, size) > width) throw new Error('A retainer value is too long to fit the supplied template. Please contact the firm.');
  field.setAlignment(alignment);
  field.setFontSize(size);
  field.setText(value);
}

function drawUnsignedRows(page, anchor, debtorNames, font) {
  const nameWidth = anchor.width;
  const top = anchor.y + anchor.height - 2;
  const bottom = 78; // Keep the original feather/footer and page number clear.
  let size = 11;
  let rows;
  for (; size >= 8; size -= 0.5) {
    const candidateSize = size;
    rows = [...debtorNames.map((name, index) => ({ name, role: `Debtor ${index + 1}` })), { name: 'Oliver Cisowski, Attorney', role: 'Attorney' }]
      .map(row => ({ ...row, lines: wrapName(row.name, font, candidateSize, nameWidth) }));
    const requiredHeight = rows.reduce((height, row) => height + Math.max(60, row.lines.length * candidateSize * 1.2 + 24), 0) + (rows.length - 1) * 10;
    if (requiredHeight <= top - bottom) break;
  }
  if (size < 8) throw new Error('The debtor names are too long to fit the unsigned signature area legibly. Please contact the firm to prepare the agreement.');
  let rowTop = top;
  for (const row of rows) {
    const height = Math.max(60, row.lines.length * size * 1.2 + 24);
    const lineY = rowTop - height + 18;
    row.lines.forEach((line, index) => page.drawText(line, {
      x: anchor.x,
      y: lineY + 7 + (row.lines.length - index - 1) * size * 1.2,
      size,
      font,
      color: rgb(0, 0, 0),
    }));
    const columns = [
      { x: anchor.x, width: nameWidth, label: row.role === 'Attorney' ? 'Printed name - Attorney' : `Printed name - ${row.role}` },
      { x: 263, width: 205, label: `${row.role} signature` },
      { x: 482, width: 94, label: 'Date' },
    ];
    for (const column of columns) {
      page.drawLine({ start: { x: column.x, y: lineY }, end: { x: column.x + column.width, y: lineY }, thickness: 0.7, color: rgb(0, 0, 0) });
      page.drawText(column.label, { x: column.x, y: lineY - 12, size: 9, font, color: rgb(0, 0, 0) });
    }
    rowTop -= height + 10;
  }
}

/** Create a local, static print copy only; never signs, uploads, or saves status. */
export async function buildUnsignedRetainerPdf({
  templateBytes,
  chapter,
  caseId,
  debtorNames,
  retainerAmount,
  totalAttorneyFee,
  preFilingFee,
  filingFeeAmount,
}) {
  const selectedChapter = String(chapter);
  if (!['7', '13'].includes(selectedChapter)) throw new Error('An unsigned retainer is available only for Chapter 7 or Chapter 13.');
  if (!Array.isArray(debtorNames) || debtorNames.length < 1 || debtorNames.length > 2
    || debtorNames.some(name => typeof name !== 'string' || !name.trim())) {
    throw new Error('Provide the full name of each filing debtor, in Debtor 1 / Debtor 2 order.');
  }
  const total = normalizeRetainerAmount(totalAttorneyFee, 'Total attorney fee');
  const pre = selectedChapter === '7' ? total : normalizeRetainerAmount(preFilingFee, 'Pre-filing fee');
  const retainer = normalizeRetainerAmount(retainerAmount, 'Retainer amount');
  const filing = normalizeRetainerAmount(filingFeeAmount, 'Filing fee');
  const totalCents = Math.round(Number(total) * 100);
  const preCents = Math.round(Number(pre) * 100);
  if (totalCents < preCents) throw new Error('Total attorney fee cannot be less than the pre-filing fee.');

  const pdf = await PDFDocument.load(templateBytes);
  if (pdf.getPageCount() !== 4) throw new Error('The retainer template must contain its original four pages.');
  const form = pdf.getForm();
  if (Boolean(form.getFieldMaybe('post_filing_fees')) !== (selectedChapter === '13')) {
    throw new Error('The retainer template does not match the selected bankruptcy chapter.');
  }
  const lastPage = pdf.getPages()[3];
  const nameField = form.getTextField('debtor_full_name');
  const widgets = nameField.acroField.getWidgets();
  const widget = widgets[widgets.length - 1];
  if (!widget || widget.P()?.toString() !== lastPage.ref.toString()) throw new Error('The retainer signature page could not be verified.');
  const anchor = widget.getRectangle();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const names = debtorNames.map(name => supportedText(font, name, 'A debtor name'));
  const caseText = supportedText(font, caseId, 'The case identifier');

  removeOriginalSignatureBlock(pdf, lastPage, anchor);
  for (const field of [...form.getFields()]) {
    if (field instanceof PDFTextField) field.setText('');
  }
  // The original signature button has no appearance stream. pdf-lib requires
  // one even when removing a field, so generate blank appearances first.
  form.updateFieldAppearances(font);
  for (const field of [...form.getFields()]) {
    if (['Date', 'debtor_full_name', 'client_signature'].includes(field.getName())) form.removeField(field);
  }
  fitFormText(form, 'case_id', caseText, font, TextAlignment.Left);
  fitFormText(form, 'pre_filing_fees', pre, font);
  fitFormText(form, 'filing_fee_amount', filing, font);
  fitFormText(form, 'down_payment', retainer, font);
  if (selectedChapter === '13') fitFormText(form, 'post_filing_fees', ((totalCents - preCents) / 100).toFixed(2), font);
  form.updateFieldAppearances(font);
  form.flatten({ updateFieldAppearances: false });
  // A static printed agreement must not retain an empty form tree or widgets.
  for (const page of pdf.getPages()) {
    const annotations = page.node.Annots();
    if (!annotations) continue;
    for (let index = annotations.size() - 1; index >= 0; index -= 1) {
      const annotation = pdf.context.lookup(annotations.get(index));
      if (!annotation || (annotation instanceof PDFDict && annotation.get(PDFName.of('Subtype'))?.toString() === '/Widget')) annotations.remove(index);
    }
    if (!annotations.size()) page.node.delete(PDFName.of('Annots'));
  }
  pdf.catalog.delete(PDFName.of('AcroForm'));
  drawUnsignedRows(lastPage, anchor, names, font);
  pdf.setTitle(`Unsigned Chapter ${selectedChapter} Retainer Agreement`);
  return pdf.save();
}
